/**
 * Charitage Foundation - Official 80G Tax Exemption Receipt Generator & Printer
 * Ensures a crisp, single-page (1-page) official receipt is printed/downloaded as PDF
 * without overflowing or printing background web pages.
 */

// Helper to convert number to Indian currency words
export const numberToWordsIndian = (num) => {
  const a = [
    '', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ',
    'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '
  ];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const inWords = (n) => {
    let str = '';
    if (n > 99) {
      str += a[Math.floor(n / 100)] + 'Hundred ';
      n %= 100;
    }
    if (n > 19) {
      str += b[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + a[n % 10] : ' ');
    } else if (n > 0) {
      str += a[n];
    }
    return str;
  };

  const amount = Math.floor(Math.abs(Number(num) || 0));
  if (amount === 0) return 'Zero Rupees Only';

  let crore = Math.floor(amount / 10000000);
  let remainder = amount % 10000000;
  let lakh = Math.floor(remainder / 100000);
  remainder %= 100000;
  let thousand = Math.floor(remainder / 1000);
  remainder %= 1000;

  let result = 'Rupees ';
  if (crore > 0) result += inWords(crore) + 'Crore ';
  if (lakh > 0) result += inWords(lakh) + 'Lakh ';
  if (thousand > 0) result += inWords(thousand) + 'Thousand ';
  if (remainder > 0) result += inWords(remainder);

  return result.trim() + ' Only';
};

export const printOfficialReceipt = (receiptData) => {
  if (!receiptData) return;

  const receiptNo = receiptData.receipt_number || `REC-${Date.now()}`;
  const dateFormatted = receiptData.date 
    ? new Date(receiptData.date).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : new Date().toLocaleDateString('en-IN');

  const donorName = receiptData.donor_name || 'Anonymous Well-Wisher';
  const donorEmail = receiptData.donor_email || 'N/A';
  const donorPhone = receiptData.donor_phone || 'N/A';
  const donorPan = (receiptData.donor_pan && receiptData.donor_pan.trim() !== '') ? receiptData.donor_pan.toUpperCase() : 'N/A';
  const campaign = receiptData.campaign_title || 'General Charitable Corpus Fund';
  const paymentId = receiptData.payment_id || 'PAY-ONLINE';
  const baseAmount = Number(receiptData.amount || 0);
  const tipAmount = Number(receiptData.tip_amount || 0);
  const totalPaid = Number(receiptData.total_paid || (baseAmount + tipAmount));
  const amountInWords = numberToWordsIndian(totalPaid);

  const receiptHtml = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Donation Receipt - ${receiptNo} - Charitage Foundation</title>
  <style>
    @page {
      size: A4 portrait;
      margin: 10mm 14mm;
    }
    * {
      box-sizing: border-box;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    body {
      margin: 0;
      padding: 0;
      font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, Helvetica, Arial, sans-serif;
      color: #1e293b;
      background: #ffffff;
      font-size: 13px;
      line-height: 1.45;
    }
    .receipt-container {
      width: 100%;
      max-width: 740px;
      margin: 0 auto;
      border: 2px solid #16225B;
      border-radius: 8px;
      padding: 24px 28px;
      position: relative;
      background: #ffffff;
    }
    .watermark {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%) rotate(-30deg);
      font-size: 78px;
      font-weight: 900;
      color: rgba(22, 34, 91, 0.035);
      letter-spacing: 12px;
      pointer-events: none;
      user-select: none;
      z-index: 0;
      white-space: nowrap;
    }
    .header-table {
      width: 100%;
      border-collapse: collapse;
      border-bottom: 2px solid #16225B;
      padding-bottom: 12px;
      margin-bottom: 14px;
    }
    .header-logo {
      width: 48%;
      vertical-align: middle;
    }
    .header-info {
      width: 52%;
      text-align: right;
      vertical-align: middle;
      font-size: 11px;
      color: #475569;
      line-height: 1.35;
    }
    .brand-title {
      font-size: 24px;
      font-weight: 800;
      color: #16225B;
      letter-spacing: 1px;
      margin: 0 0 2px 0;
    }
    .brand-motto {
      font-size: 10px;
      font-weight: 700;
      color: #D97706;
      letter-spacing: 2px;
      text-transform: uppercase;
    }
    .title-banner {
      background: #16225B;
      color: #ffffff;
      text-align: center;
      padding: 8px 12px;
      border-radius: 4px;
      margin-bottom: 14px;
    }
    .title-banner h2 {
      margin: 0;
      font-size: 14px;
      font-weight: 700;
      letter-spacing: 1.5px;
      text-transform: uppercase;
    }
    .title-banner p {
      margin: 2px 0 0 0;
      font-size: 10px;
      opacity: 0.9;
    }
    .meta-grid {
      width: 100%;
      border-collapse: collapse;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      margin-bottom: 14px;
    }
    .meta-grid td {
      padding: 6px 12px;
      font-size: 11.5px;
      border-bottom: 1px solid #e2e8f0;
    }
    .meta-label {
      color: #64748b;
      font-weight: 600;
      width: 22%;
    }
    .meta-val {
      color: #0f172a;
      font-weight: 700;
      width: 28%;
    }
    .section-title {
      font-size: 12px;
      font-weight: 700;
      color: #16225B;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      margin: 10px 0 6px 0;
      border-bottom: 1px solid #cbd5e1;
      padding-bottom: 4px;
    }
    .data-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 12px;
    }
    .data-table th {
      background: #f1f5f9;
      color: #1e293b;
      font-size: 11px;
      text-transform: uppercase;
      font-weight: 700;
      text-align: left;
      padding: 6px 10px;
      border: 1px solid #cbd5e1;
    }
    .data-table td {
      padding: 7px 10px;
      border: 1px solid #e2e8f0;
      font-size: 12px;
    }
    .text-right {
      text-align: right;
    }
    .text-center {
      text-align: center;
    }
    .total-row td {
      font-weight: 800;
      background: #f8fafc;
      border-top: 2px solid #16225B;
      font-size: 13px;
      color: #16225B;
    }
    .words-box {
      background: #fffbeb;
      border: 1px dashed #d97706;
      border-radius: 4px;
      padding: 8px 12px;
      margin-bottom: 14px;
      font-size: 12px;
    }
    .words-box strong {
      color: #92400e;
    }
    .tax-box {
      background: #f0fdf4;
      border-left: 4px solid #16a34a;
      padding: 8px 12px;
      font-size: 10.5px;
      color: #14532d;
      margin-bottom: 16px;
      line-height: 1.4;
    }
    .signatures-table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 14px;
    }
    .signatures-table td {
      vertical-align: bottom;
      padding: 0;
    }
    .seal-box {
      width: 110px;
      height: 70px;
      border: 2px dashed #94a3b8;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      font-size: 9px;
      color: #64748b;
      font-weight: 700;
      text-transform: uppercase;
      line-height: 1.2;
      padding: 4px;
    }
    .sign-box {
      text-align: right;
    }
    .sign-img {
      height: 38px;
      margin-bottom: 2px;
    }
    .sign-line {
      display: inline-block;
      width: 180px;
      border-top: 1px solid #0f172a;
      margin-top: 30px;
      text-align: center;
      padding-top: 4px;
      font-size: 11px;
      font-weight: 700;
      color: #0f172a;
    }
    .footer-note {
      text-align: center;
      font-size: 9.5px;
      color: #94a3b8;
      border-top: 1px solid #e2e8f0;
      padding-top: 10px;
      margin-top: 14px;
    }
  </style>
</head>
<body>
  <div class="receipt-container">
    <div class="watermark">CHARITAGE 80G</div>

    <!-- Header -->
    <table class="header-table">
      <tr>
        <td class="header-logo">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 320" style="height: 52px; width: auto; max-width: 100%; display: block;" aria-label="Charitage Foundation">
            <g transform="translate(15, 10)">
              <!-- Red Heart Shape -->
              <path d="M 145 75 C 110 35, 60 40, 45 80 C 25 130, 60 170, 145 220 C 230 170, 265 130, 245 80 C 230 40, 180 35, 145 75 Z" fill="none" stroke="#E31E24" stroke-width="26" stroke-linecap="round" stroke-linejoin="round" />
              <!-- Supporting Dark Blue Hand -->
              <path d="M 20 190 C 30 160, 70 145, 130 165 C 170 178, 205 160, 245 135 C 258 148, 265 162, 242 180 C 215 200, 195 215, 175 210 C 155 205, 125 210, 85 225 C 45 240, 20 220, 20 190 Z" fill="#222566" />
              <path d="M 175 190 C 195 170, 235 155, 270 165 C 260 180, 245 190, 220 195 Z" fill="#222566" />
              <!-- Human Silhouette Figure Inside Heart -->
              <circle cx="145" cy="115" r="16" fill="#222566" />
              <path d="M 120 165 C 120 140, 115 130, 105 130 C 125 130, 135 145, 145 152 C 155 145, 165 130, 185 130 C 175 130, 170 140, 170 165 Z" fill="#222566" />
            </g>
            <!-- Right Text Group -->
            <g transform="translate(285, 30)">
              <text x="0" y="115" style="font-family: 'Merriweather', 'Cinzel', 'Times New Roman', serif; font-weight: 700; font-size: 110px; fill: #222566; letter-spacing: 2px;">CHARITAGE</text>
              <g transform="translate(0, 175)">
                <line x1="0" y1="-14" x2="140" y2="-14" stroke="#222566" stroke-width="3" />
                <text x="155" y="0" style="font-family: 'Inter', 'Montserrat', 'Segoe UI', Arial, sans-serif; font-weight: 700; font-size: 46px; fill: #00AEEF; letter-spacing: 12px;">FOUNDATION</text>
                <line x1="565" y1="-14" x2="700" y2="-14" stroke="#222566" stroke-width="3" />
              </g>
              <text x="80" y="240" style="font-family: 'Inter', 'Montserrat', 'Segoe UI', Arial, sans-serif; font-weight: 700; font-size: 30px; fill: #222566; letter-spacing: 7px;">LEADING WITH KINDNESS</text>
            </g>
          </svg>
          <div style="font-size: 9.5px; color: #64748b; margin-top: 4px; font-weight: 500; line-height: 1.25;">
            Registered Public Charitable Trust under Bombay Public Trust Act, 1950
          </div>
        </td>
        <td class="header-info">
          <div style="font-size: 13px; font-weight: 800; color: #16225B; margin-bottom: 2px; letter-spacing: 0.3px;">CHARITAGE FOUNDATION</div>
          <div style="font-size: 10.5px; color: #475569; margin-bottom: 3px; line-height: 1.35;">
            Plot number 01, H. No. 2368, Wathoda, Bagadganj, Nagpur, Maharashtra, India, 440008
          </div>
          <div style="font-size: 11px; color: #334155; margin-bottom: 3px;">
            <strong>Email:</strong> info@charitage.org &nbsp;|&nbsp; <strong>Website:</strong> <a href="https://charitage.org" target="_blank" rel="noopener noreferrer" style="color: #0284c7; font-weight: 700; text-decoration: none;">https://charitage.org</a>
          </div>
          <div style="font-size: 11px; color: #1e293b;">
            <strong>PAN:</strong> AANCC6167A &nbsp;|&nbsp; <strong>80G Unique Reg. No:</strong> AANCC6167AF20261
          </div>
        </td>
      </tr>
    </table>

    <!-- Certificate Title -->
    <div class="title-banner">
      <h2>DONATION RECEIPT &bull; FORM 10BE CERTIFICATE</h2>
      <p>Eligible for 50% Deduction under Section 80G(5)(vi) of the Income Tax Act, 1961</p>
    </div>

    <!-- Receipt Meta -->
    <table class="meta-grid">
      <tr>
        <td class="meta-label">Receipt Number:</td>
        <td class="meta-val" style="color: #16225B;">${receiptNo}</td>
        <td class="meta-label">Receipt Date:</td>
        <td class="meta-val">${dateFormatted}</td>
      </tr>
      <tr>
        <td class="meta-label">Transaction Ref:</td>
        <td class="meta-val" style="font-family: monospace;">${paymentId}</td>
        <td class="meta-label">Payment Mode:</td>
        <td class="meta-val">Online (Razorpay Gateway)</td>
      </tr>
    </table>

    <!-- Donor Information -->
    <div class="section-title">Donor &amp; Contribution Particulars</div>
    <table class="data-table" style="margin-bottom: 12px;">
      <tr>
        <td style="width: 25%; font-weight: 600; color: #475569;">Donor Name</td>
        <td style="font-weight: 700; color: #0f172a;">${donorName}</td>
        <td style="width: 20%; font-weight: 600; color: #475569;">Donor PAN</td>
        <td style="font-weight: 800; color: #16225B; font-family: monospace;">${donorPan}</td>
      </tr>
      <tr>
        <td style="font-weight: 600; color: #475569;">Email Address</td>
        <td>${donorEmail}</td>
        <td style="font-weight: 600; color: #475569;">Phone Number</td>
        <td>${donorPhone}</td>
      </tr>
      <tr>
        <td style="font-weight: 600; color: #475569;">Purpose / Cause</td>
        <td colspan="3" style="font-weight: 600; color: #0f172a;">${campaign}</td>
      </tr>
    </table>

    <!-- Financial Breakdown -->
    <div class="section-title">Financial Details</div>
    <table class="data-table">
      <thead>
        <tr>
          <th style="width: 8%;" class="text-center">#</th>
          <th>Description of Contribution</th>
          <th style="width: 25%;" class="text-right">Amount (INR)</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="text-center">1</td>
          <td>
            <strong>Direct Donation towards ${campaign}</strong><br />
            <span style="font-size: 10.5px; color: #64748b;">100% credited to ground beneficiaries (0% administrative deduction)</span>
          </td>
          <td class="text-right" style="font-weight: 700;">&#8377; ${baseAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
        </tr>
        ${tipAmount > 0 ? `
        <tr>
          <td class="text-center">2</td>
          <td>
            <strong>Voluntary Foundation Support Tip</strong><br />
            <span style="font-size: 10.5px; color: #64748b;">Donor voluntary operational contribution</span>
          </td>
          <td class="text-right" style="font-weight: 700;">&#8377; ${tipAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
        </tr>
        ` : ''}
        <tr class="total-row">
          <td colspan="2" class="text-right">Total Net Contribution Received:</td>
          <td class="text-right">&#8377; ${totalPaid.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
        </tr>
      </tbody>
    </table>

    <!-- Amount in Words -->
    <div class="words-box">
      <strong>Amount in Words:</strong> ${amountInWords}
    </div>

    <!-- Statutory 80G Note -->
    <div class="tax-box">
      <strong>Statutory Tax Deduction Clause:</strong><br />
      Donations made to <strong>Charitage Foundation</strong> are exempt under Section 80G(5) of the Income Tax Act, 1961 vide Unique Registration Number (URN) <strong>AANCC6167AF20261</strong> (Approval Order DIN: <strong>AANCC6167AF2026102</strong>). Donor PAN specified above will be included in the annual statement Form 10BD filed with the Income Tax Department for auto-generation of Form 10BE in your Income Tax Portal.
    </div>

    <!-- Signatures -->
    <table class="signatures-table">
      <tr>
        <td style="width: 50%;">
          <div class="seal-box">
            Charitage Foundation<br />
            Official Seal<br />
            [Verified]
          </div>
        </td>
        <td style="width: 50%; text-align: right;">
          <div class="sign-line">
            Authorized Signatory<br />
            <span style="font-size: 9.5px; color: #64748b; font-weight: normal;">Finance &amp; Trustee Operations</span>
          </div>
        </td>
      </tr>
    </table>

    <!-- Footer Note -->
    <div class="footer-note">
      This is a digitally verified computer-generated receipt issued pursuant to Rule 18AB of the Income Tax Rules. No physical signature is required.<br />
      For any verification or queries, please visit <strong>https://charitage.org</strong> or email <strong>info@charitage.org</strong> quoting Receipt Ref: <strong>${receiptNo}</strong>.
    </div>
  </div>
</body>
</html>
`;

  // Create isolated, hidden iframe to trigger a dedicated 1-PAGE print
  const existingFrame = document.getElementById('charitage-print-iframe');
  if (existingFrame) {
    existingFrame.remove();
  }

  const iframe = document.createElement('iframe');
  iframe.id = 'charitage-print-iframe';
  iframe.style.position = 'fixed';
  iframe.style.top = '-9999px';
  iframe.style.left = '-9999px';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';

  document.body.appendChild(iframe);

  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(receiptHtml);
  doc.close();

  iframe.contentWindow.focus();

  // Give brief moment for fonts/rendering, then trigger print
  setTimeout(() => {
    try {
      iframe.contentWindow.print();
    } catch (err) {
      // Fallback: Open clean popup window if iframe printing is restricted by browser
      const printWin = window.open('', '_blank', 'width=800,height=900');
      if (printWin) {
        printWin.document.open();
        printWin.document.write(receiptHtml);
        printWin.document.close();
        printWin.focus();
        setTimeout(() => {
          printWin.print();
        }, 500);
      }
    }
  }, 350);
};

export default printOfficialReceipt;
